// Utility functions for shape detection and connection management

// Snap threshold in pixels
const SNAP_THRESHOLD = 15;

/**
 * Check if two points are close enough to snap together
 */
export const arePointsClose = (point1, point2, threshold = SNAP_THRESHOLD) => {
  const distance = Math.sqrt(
    Math.pow(point1.x - point2.x, 2) + Math.pow(point1.y - point2.y, 2)
  );
  return distance <= threshold;
};

/**
 * Find nearby endpoints for snapping
 */
export const findNearbyEndpoints = (currentShape, currentNodeIndex, allShapes) => {
  // Add null checks
  if (!allShapes || !Array.isArray(allShapes)) {
    console.warn('findNearbyEndpoints: allShapes is not a valid array:', allShapes);
    return null;
  }

  const currentShapeData = allShapes.find(s => s.id === currentShape.id);
  if (!currentShapeData || !currentShapeData.nodes) {
    console.warn('findNearbyEndpoints: currentShapeData not found or has no nodes:', currentShapeData);
    return null;
  }

  const currentNode = currentShapeData.nodes[currentNodeIndex];
  if (!currentNode) {
    console.warn('findNearbyEndpoints: currentNode not found at index:', currentNodeIndex);
    return null;
  }

  const currentGlobalPoint = {
    x: currentShapeData.x + currentNode.x,
    y: currentShapeData.y + currentNode.y
  };

  let closestEndpoint = null;
  let minDistance = Infinity;

  allShapes.forEach(shape => {
    if (shape.id === currentShape.id || shape.type !== 'line') return;

    // Check start point (node 0)
    const startPoint = {
      x: shape.x + shape.nodes[0].x,
      y: shape.y + shape.nodes[0].y
    };
    const startDistance = Math.sqrt(
      Math.pow(currentGlobalPoint.x - startPoint.x, 2) + 
      Math.pow(currentGlobalPoint.y - startPoint.y, 2)
    );

    if (startDistance < minDistance && startDistance <= SNAP_THRESHOLD) {
      minDistance = startDistance;
      closestEndpoint = {
        shapeId: shape.id,
        nodeIndex: 0,
        point: startPoint,
        distance: startDistance
      };
    }

    // Check end point (last node)
    const endPoint = {
      x: shape.x + shape.nodes[shape.nodes.length - 1].x,
      y: shape.y + shape.nodes[shape.nodes.length - 1].y
    };
    const endDistance = Math.sqrt(
      Math.pow(currentGlobalPoint.x - endPoint.x, 2) + 
      Math.pow(currentGlobalPoint.y - endPoint.y, 2)
    );

    if (endDistance < minDistance && endDistance <= SNAP_THRESHOLD) {
      minDistance = endDistance;
      closestEndpoint = {
        shapeId: shape.id,
        nodeIndex: shape.nodes.length - 1,
        point: endPoint,
        distance: endDistance
      };
    }
  });

  return closestEndpoint;
};

/**
 * Build a graph from connected lines for cycle detection
 */
export const buildConnectionGraph = (shapes, connections) => {
  console.log('🔍 buildConnectionGraph called with:', {
    shapesCount: shapes?.length,
    connectionsCount: connections?.length
  });

  const graph = new Map();

  // Initialize graph with all shapes
  shapes.forEach(shape => {
    if (shape.type === 'line') {
      graph.set(shape.id, []);
    }
  });

  console.log('🔍 Initialized graph with line shapes:', Array.from(graph.keys()));

  // Add connections to graph
  connections.forEach(connection => {
    const { shapeId1, nodeIndex1, shapeId2, nodeIndex2 } = connection;
    
    console.log('🔍 Processing connection:', connection);
    
    if (!graph.has(shapeId1)) graph.set(shapeId1, []);
    if (!graph.has(shapeId2)) graph.set(shapeId2, []);
    
    graph.get(shapeId1).push({ shapeId: shapeId2, nodeIndex: nodeIndex2 });
    graph.get(shapeId2).push({ shapeId: shapeId1, nodeIndex: nodeIndex1 });
    
    console.log('🔍 Added connection to graph:', {
      shapeId1,
      shapeId2,
      graphShape1: graph.get(shapeId1),
      graphShape2: graph.get(shapeId2)
    });
  });

  console.log('🔍 Final graph:', graph);
  return graph;
};

/**
 * Detect cycles (closed shapes) in the connection graph
 */
export const detectClosedShapes = (shapes, connections) => {
  console.log('🔍 detectClosedShapes called with:', {
    shapesCount: shapes?.length,
    connectionsCount: connections?.length,
    shapes: shapes?.map(s => ({ id: s.id, type: s.type })),
    connections
  });

  // Extra debug: log all connections
  console.log('🔍 All connections:', JSON.stringify(connections, null, 2));

  const graph = buildConnectionGraph(shapes, connections);
  console.log('🔍 Built connection graph:', graph);
  
  const visited = new Set();
  const cycles = [];

  // Helper to get a unique key for a cycle (ignoring node indices)
  const cycleKey = (cycle) => cycle.map(n => n.shapeId).sort().join('-');

  const dfs = (startShapeId, currentShapeId, path) => {
    if (path.length > 0 && currentShapeId === startShapeId) {
      // Found a cycle
      const cycle = [...path];
      const key = cycleKey(cycle);
      
      console.log('🔍 Found potential cycle:', {
        cycle,
        key,
        pathLength: path.length
      });
      
      // Check if this cycle is unique (ignore node indices)
      const isUnique = !cycles.some(existingCycle => cycleKey(existingCycle) === key);

      if (isUnique && cycle.length >= 3) {
        console.log('🔍 Adding unique cycle:', cycle);
        cycles.push(cycle);
      } else {
        console.log('🔍 Cycle rejected:', { isUnique, cycleLength: cycle.length, key });
      }
      return;
    }

    if (visited.has(currentShapeId)) return;
    visited.add(currentShapeId);

    const neighbors = graph.get(currentShapeId) || [];
    console.log('🔍 DFS neighbors for', currentShapeId, ':', neighbors);
    
    for (const neighbor of neighbors) {
      const nextPath = [...path, { shapeId: currentShapeId }]; // Ignore nodeIndex
      dfs(startShapeId, neighbor.shapeId, nextPath);
    }

    visited.delete(currentShapeId);
  };

  // Start DFS from each shape
  for (const [shapeId] of graph) {
    if (!visited.has(shapeId)) {
      console.log('🔍 Starting DFS from shape:', shapeId);
      dfs(shapeId, shapeId, []);
    }
  }

  console.log('🔍 Final cycles detected:', cycles);
  return cycles;
};

/**
 * Calculate vertices for a closed shape from its line IDs
 */
export const calculateShapeVertices = (cycle, shapes) => {
  const vertices = [];
  const shapeMap = new Map(shapes.map(s => [s.id, s]));

  for (const node of cycle) {
    const shape = shapeMap.get(node.shapeId);
    if (!shape || !shape.nodes) continue;

    const nodeData = shape.nodes[node.nodeIndex];
    if (!nodeData) continue;

    vertices.push({
      x: shape.x + nodeData.x,
      y: shape.y + nodeData.y
    });
  }

  return vertices;
};

/**
 * Check if a point is inside a polygon (for fill rendering)
 */
export const isPointInPolygon = (point, polygon) => {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const xi = polygon[i].x, yi = polygon[i].y;
    const xj = polygon[j].x, yj = polygon[j].y;
    
    if (((yi > point.y) !== (yj > point.y)) &&
        (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Generate a unique color for closed shapes
 */
export const generateShapeColor = (index) => {
  const colors = [
    '#e3f2fd', // Light blue
    '#f3e5f5', // Light purple
    '#e8f5e8', // Light green
    '#fff3e0', // Light orange
    '#fce4ec', // Light pink
    '#f1f8e9', // Light lime
    '#e0f2f1', // Light teal
    '#fafafa'  // Light gray
  ];
  return colors[index % colors.length];
}; 